import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Refresh,
  SignalCellular4Bar,
  SignalCellular0Bar,
} from '@mui/icons-material';
import useLiveGoldPrice from '../hooks/useLiveGoldPrice';
import useMlPredictions from '../hooks/useMlPredictions';

const fmt = (n) =>
  n == null
    ? '—'
    : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Live market spot + session OHLC from ML file.
 */
const LiveMarketPrice = () => {
  const live = useLiveGoldPrice(30000);
  const { data: ml } = useMlPredictions();

  const display =
    live.price ??
    ml?.live?.price ??
    ml?.current?.close ??
    null;

  const up = (live.change ?? 0) >= 0;

  return (
    <Box>
      {live.error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {live.error}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 3,
              textAlign: 'center',
              bgcolor: '#1A1A1A',
              border: '2px solid',
              borderColor: 'primary.main',
            }}
          >
            <Typography variant="overline" color="primary.main" fontWeight="bold">
              Live Market Spot (XAUUSD)
            </Typography>
            {live.loading && display == null ? (
              <Box py={3}><CircularProgress /></Box>
            ) : (
              <>
                <Typography
                  variant="h2"
                  fontWeight="bold"
                  color="primary.main"
                  sx={{ fontSize: { xs: '2rem', md: '3rem' } }}
                >
                  {fmt(display)}
                </Typography>
                <Box display="flex" justifyContent="center" alignItems="center" gap={1} my={1}>
                  {up ? <TrendingUp color="success" /> : <TrendingDown color="error" />}
                  <Typography variant="h6" color={up ? 'success.main' : 'error.main'} fontWeight="bold">
                    {live.change >= 0 ? '+' : ''}{live.change ?? 0}
                    {' '}({live.changePercent >= 0 ? '+' : ''}{live.changePercent ?? 0}%)
                  </Typography>
                </Box>
              </>
            )}
            <Box display="flex" justifyContent="center" gap={1} flexWrap="wrap" mt={1}>
              <Chip
                size="small"
                icon={live.price ? <SignalCellular4Bar /> : <SignalCellular0Bar />}
                label={live.source ? `Live · ${live.source}` : ml?.live?.source ? `File · ${ml.live.source}` : 'History'}
                color={live.price ? 'success' : 'warning'}
                variant="outlined"
              />
              <Button
                size="small"
                startIcon={live.loading ? <CircularProgress size={14} /> : <Refresh />}
                onClick={live.refresh}
                disabled={live.loading}
              >
                Refresh
              </Button>
            </Box>
            {live.updatedAt && (
              <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                Updated {live.updatedAt.toLocaleTimeString()}
              </Typography>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Grid container spacing={1}>
            {[
              ['Session Open', ml?.current?.open],
              ['Session High', ml?.current?.high],
              ['Session Low', ml?.current?.low],
              ['Last Close', ml?.current?.close],
            ].map(([label, val]) => (
              <Grid item xs={6} key={label}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#1A1A1A', border: '1px solid #333' }}>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                  <Typography variant="h6" fontWeight="bold">{fmt(val)}</Typography>
                </Paper>
              </Grid>
            ))}
            {ml?.current?.date && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" textAlign="center" display="block">
                  Last completed session: {ml.current.date}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default LiveMarketPrice;
